// EndMeeting.js - Dialog Component for Ending Meetings
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  AccountBalance,
  Cancel,
  CheckCircle,
  Warning,
  CalendarToday,
  Schedule,
  TrendingUp,
  MoneyOff,
  AccountBalanceWallet,
  Payment,
  Sync
} from '@mui/icons-material';

const EndMeeting = ({ 
  open, 
  onClose, 
  meeting, 
  onEndMeeting, 
  loading = false,
  advanceFromOffice = 0
}) => {
  const [formData, setFormData] = useState({
    confirmReceived: false,
    notes: ''
  });
  
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && meeting) {
      setFormData({
        confirmReceived: false,
        notes: ''
      });
      setErrors({});
      loadFinancialSummary();
    }
  }, [open, meeting]);

  const loadFinancialSummary = async () => {
    if (!meeting?.activeMeetingId) return;
    
    setLoadingSummary(true);
    try {
      const { diaryAPI } = await import('./api');
      const response = await diaryAPI.meetings.getMeetingFinancialSummary(meeting.activeMeetingId);
      setFinancialSummary(response.financial_summary);
    } catch (error) {
      console.error('Failed to load financial summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.confirmReceived) {
      newErrors.confirmReceived = 'You must confirm receipt of all amounts';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const endMeetingData = {
      secretary_notes: formData.notes,
      confirmed_by_secretary: formData.confirmReceived
    };

    onEndMeeting(endMeetingData);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `KES ${(amount || 0).toLocaleString()}`;
  };

  const currentMeeting = meeting?.meetings?.[0] || {};
  const periodStart = currentMeeting.period_start || meeting?.periodStart;
  const periodEnd = currentMeeting.meeting_date || currentMeeting.period_end || meeting?.meetings?.[0]?.date;

  if (!meeting) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <CheckCircle sx={{ color: 'success.main' }} />
        <Box>
          <Typography variant="h6" component="div">
            End Meeting Confirmation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {meeting.groupName} - {meeting.groupId}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Sync Warning */}
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Sync />}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Important: Ensure All Data is Synchronized
          </Typography>
          <Typography variant="body2">
            Before ending this meeting, please ensure that all field officers have synchronized their offline data. 
            This ensures accurate calculation of amounts to be returned to the office.
          </Typography>
        </Alert>

        {/* Meeting Period Information */}
        <Card sx={{ 
          mb: 3, 
          bgcolor: 'primary.dark',
          border: 1,
          borderColor: 'primary.main'
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              color: 'primary.light',
              display: 'flex', 
              alignItems: 'center', 
              gap: 1 
            }}>
              <Schedule />
              Meeting Period
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 2, 
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Period Start
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatDate(periodStart)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 2, 
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Period End (Meeting Date)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatDate(periodEnd)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Financial Summary */}
        {loadingSummary ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading financial summary...</Typography>
          </Box>
        ) : financialSummary ? (
          <Card sx={{ 
            mb: 3, 
            bgcolor: 'success.dark',
            border: 2,
            borderColor: 'success.main'
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                color: 'success.light',
                display: 'flex', 
                alignItems: 'center', 
                gap: 1 
              }}>
                <AccountBalance />
                Financial Summary - Amount Expected from Field Officer
              </Typography>
              
              <Grid container spacing={3}>
                {/* Net Cash Remitted */}
                <Grid item xs={12}>
                  <Paper sx={{ 
                    p: 3, 
                    bgcolor: 'success.main',
                    border: 2,
                    borderColor: 'success.light',
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" color="success.contrastText" sx={{ mb: 1, fontWeight: 600 }}>
                      NET CASH TO BE REMITTED TO OFFICE
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.contrastText' }}>
                      {formatCurrency(financialSummary.net_cash_remitted)}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      mt: 1, 
                      display: 'block',
                      color: 'success.contrastText',
                      opacity: 0.9
                    }}>
                      This is the amount the field officer should bring to the office
                    </Typography>
                  </Paper>
                </Grid>

                {/* Breakdown */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider'
                  }}>
                    <AccountBalanceWallet sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Cash Collected
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatCurrency(financialSummary.cash_collected)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider'
                  }}>
                    <Payment sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Advance from Office
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {formatCurrency(financialSummary.requested_advance_loans || advanceFromOffice)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider'
                  }}>
                    <TrendingUp sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Advances Given
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      {formatCurrency(financialSummary.advances_given)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider'
                  }}>
                    <MoneyOff sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Rejected Approved Loans
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                      {formatCurrency(financialSummary.rejected_approved_loans)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Calculation Formula */}
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                bgcolor: 'warning.dark',
                border: 1,
                borderColor: 'warning.main',
                borderRadius: 1
              }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'warning.light' }}>
                  Calculation Formula:
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontFamily: 'monospace',
                  color: 'warning.contrastText'
                }}>
                  Net Cash Remitted = (Cash Collected + Advance from Office + Rejected Approved Loans) - Advances Given
                </Typography>
                <Typography variant="caption" sx={{ 
                  mt: 1, 
                  display: 'block',
                  fontStyle: 'italic',
                  color: 'warning.contrastText',
                  opacity: 0.8
                }}>
                  Note: Actual calculation is performed by the backend system
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load financial summary. Please refresh and try again.
          </Alert>
        )}

        {/* Amount Verification Warning */}
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Amount Verification Required
          </Typography>
          <Typography variant="body2">
            If the amount brought by the field officer does not match the calculated net cash remitted amount above, 
            <strong> DO NOT proceed with ending this meeting</strong>. The discrepancy must be resolved first through 
            proper reconciliation and data verification.
          </Typography>
        </Alert>

        {/* Notes Section */}
        <TextField
          fullWidth
          label="Meeting Notes (Optional)"
          multiline
          rows={3}
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder={`Any additional comments or observations about the meeting period (${formatDate(periodStart)} to ${formatDate(periodEnd)})...`}
          sx={{ mb: 3 }}
        />

        {/* Confirmation */}
        <Paper sx={{ 
          p: 3, 
          bgcolor: 'warning.dark',
          border: 2,
          borderColor: 'warning.main'
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.confirmReceived}
                onChange={(e) => handleInputChange('confirmReceived', e.target.checked)}
                color="primary"
                size="large"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Secretary Confirmation
                </Typography>
                <Typography variant="body2">
                  I confirm that:
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  <Typography component="li" variant="body2">
                    All offline data has been synchronized by field officers
                  </Typography>
                  <Typography component="li" variant="body2">
                    The field officer has brought exactly <strong>{financialSummary ? formatCurrency(financialSummary.net_cash_remitted) : 'KES 0'}</strong> to the office
                  </Typography>
                  <Typography component="li" variant="body2">
                    All amounts have been verified and match the calculations
                  </Typography>
                  <Typography component="li" variant="body2">
                    I understand this meeting cannot be reopened once ended
                  </Typography>
                </Box>
              </Box>
            }
          />
          {errors.confirmReceived && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.confirmReceived}
            </Alert>
          )}
        </Paper>

        {/* Final Warning Message */}
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Final Check:</strong> Once you end this meeting, the period will be closed and cannot be reopened. 
            Ensure all financial data is accurate and reconciled before proceeding.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        gap: 2
      }}>
        <Button 
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading || !formData.confirmReceived || !financialSummary}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          sx={{ minWidth: 180 }}
        >
          {loading ? 'Ending Meeting...' : 'Confirm & End Meeting'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EndMeeting;